import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase/client";
import type { User } from "@shared/schema";

export function useAuth() {
  const { data: user, isLoading } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    retry: false,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      // First, check for Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // User has Supabase session, fetch profile from backend with token
        const supabaseRes = await fetch("/api/auth/user", {
          credentials: "include",
          cache: "no-store",
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (supabaseRes.status === 401) {
          return null;
        }

        if (!supabaseRes.ok) {
          throw new Error("Failed to fetch user");
        }

        const data = await supabaseRes.json();
        console.log("🔍 User data received from Supabase auth:", data.user);
        return data.user;
      }

      // If no Supabase session, try custom auth
      const res = await fetch("/api/auth/user", {
        credentials: "include",
        cache: "no-store",
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        }
      });

      // If backend returns a user (custom auth session exists)
      if (res.ok) {
        const data = await res.json();
        console.log("🔍 User data received from custom auth:", data.user);
        return data.user;
      }

      // No session in either system
      return null;
    },
  });

  return {
    user: user || undefined,
    isLoading,
    isAuthenticated: !!user,
  };
}
